/**
 * VisaoGeral — cartões-resumo do período (o bloco `resumo` da API: execuções distintas,
 * mediana e p90 de todas as invocações) e o ranking de agentes (ordenável por custo, erro
 * e retrabalho). Clicar no agente abre o detalhe.
 */
import { useMemo, useState } from 'react';
import { C } from '@/theme/palette';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { tokensCompactos } from '@/lib/format';
import { useObsAgentes } from '@/api/observability';
import { decimal, duracao, inteiro, ordenar, pct, rotulo } from './formato';
import { AvisoSemCusto, Custo, Kpi, Nota, Selo, Titulo, Vazio, estilos } from './partes';

const COLUNAS = [
  { campo: 'agent_name', label: 'Agente', num: false },
  { campo: 'invocacoes', label: 'Invocações' },
  { campo: 'execucoes', label: 'Execuções' },
  { campo: 'projetos', label: 'Projetos' },
  { campo: 'custo_total_usd', label: 'Custo total', custo: true },
  { campo: 'custo_medio_usd', label: 'Custo médio', custo: true },
  { campo: 'duracao_mediana_ms', label: 'Duração mediana' },
  { campo: 'duracao_p90_ms', label: 'Duração p90' },
  { campo: 'taxa_de_erro_pct', label: 'Taxa de erro' },
  { campo: 'reforcos_por_etapa', label: 'Reforços / etapa' },
  { campo: 'portao_fechado_na_1a_rodada_pct', label: 'Portão na 1ª' },
];

export function VisaoGeral({ filtro, aoAbrirAgente }) {
  const { data, isLoading, isError, error, refetch } = useObsAgentes(filtro);
  const [ordem, setOrdem] = useState({ campo: 'custo_total_usd', direcao: 'desc' });
  const s = estilos();

  const agentes = useMemo(() => data?.agentes || [], [data]);
  const visiveis = data?.custos_visiveis !== false;
  // Sem permissão de custo, a ordenação por custo não diz nada: cai para invocações.
  const campoEfetivo = !visiveis && COLUNAS.find((c) => c.campo === ordem.campo)?.custo
    ? 'invocacoes' : ordem.campo;
  const linhas = useMemo(() => ordenar(agentes, campoEfetivo, ordem.direcao),
    [agentes, campoEfetivo, ordem.direcao]);
  const r = data?.resumo || {};

  if (isLoading) return <LoadingSpinner label="Carregando agentes..." />;
  if (isError) return <QueryError error={error} onRetry={refetch} />;

  const alternar = (campo) => setOrdem((o) => (o.campo === campo
    ? { campo, direcao: o.direcao === 'desc' ? 'asc' : 'desc' }
    : { campo, direcao: campo === 'agent_name' ? 'asc' : 'desc' }));

  return (
    <div data-testid="obs-visao-geral">
      {!visiveis ? <AvisoSemCusto /> : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Kpi testId="kpi-invocacoes" label="Invocações" value={inteiro(r.invocacoes)}
          sub={`${inteiro(r.agentes)} agente(s) · ${tokensCompactos(r.input_tokens)} ent / ${tokensCompactos(r.output_tokens)} saí`} />
        <Kpi testId="kpi-execucoes" label="Execuções" value={inteiro(r.execucoes)}
          sub={`distintas · ${inteiro(r.projetos)} projeto(s)`} />
        <Kpi testId="kpi-custo" label="Custo total (US$)"
          value={<Custo valor={r.custo_total_usd} visivel={visiveis} completo />}
          sub={visiveis ? <>médio por invocação: <Custo valor={r.custo_medio_usd} /></> : null} />
        <Kpi testId="kpi-duracao" label="Duração mediana" value={duracao(r.duracao_mediana_ms)}
          sub={`p90: ${duracao(r.duracao_p90_ms)} · de todas as invocações`} />
        <Kpi testId="kpi-erro" label="Taxa de erro" value={pct(r.taxa_de_erro_pct)}
          sub={`${inteiro(r.erros)} erro(s) em ${inteiro(r.invocacoes)}`} />
        <Kpi testId="kpi-portao" label="Portão fechado na 1ª rodada" value={pct(r.portao_fechado_na_1a_rodada_pct)}
          sub={`${inteiro(r.etapas)} etapa(s) · ${inteiro(r.reforcos)} reforço(s)`} />
      </div>
      <Nota>
        Execução excluída fica fora destes números. Uma execução passa por vários agentes:
        “Execuções” conta cada uma uma vez só, por isso não é a soma da coluna do ranking.
      </Nota>

      <Titulo lado={`${inteiro(data?.total ?? agentes.length)} agente(s) no período`}>
        Ranking de agentes
      </Titulo>

      {agentes.length === 0 ? (
        <Vazio>
          Nenhuma invocação de agente registrada neste período{filtro?.projectId ? ' para este projeto' : ''}.
          O registro começa nas execuções feitas depois da migration 0101.
        </Vazio>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.tabela}>
            <thead>
              <tr>
                {COLUNAS.map((c) => {
                  const ativa = campoEfetivo === c.campo;
                  return (
                    <th key={c.campo} style={c.num === false ? s.th : s.thNum}
                      aria-sort={ativa ? (ordem.direcao === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button type="button" onClick={() => alternar(c.campo)}
                        aria-label={`Ordenar por ${c.label}`}
                        style={{ all: 'unset', cursor: 'pointer', color: ativa ? C.accent : 'inherit', font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>
                        {c.label}{ativa ? (ordem.direcao === 'asc' ? ' ▲' : ' ▼') : ''}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {linhas.map((a) => (
                <tr key={a.agent_name} data-testid="linha-agente-obs">
                  <td style={{ ...s.td, wordBreak: 'break-word', minWidth: 170 }}>
                    <button type="button" style={s.link} onClick={() => aoAbrirAgente(a.agent_name)}
                      aria-label={`Abrir o detalhe do agente ${a.agent_name}`}>
                      {a.agent_name}
                    </button>
                    {a.papel_origem ? <div style={{ marginTop: 3 }}><Selo>{rotulo(a.papel_origem)}</Selo></div> : null}
                  </td>
                  <td style={s.tdNum}>{inteiro(a.invocacoes)}</td>
                  <td style={s.tdNum}>{inteiro(a.execucoes)}</td>
                  <td style={s.tdNum}>{inteiro(a.projetos)}</td>
                  <td style={s.tdNum}><Custo valor={a.custo_total_usd} visivel={visiveis} /></td>
                  <td style={s.tdNum}><Custo valor={a.custo_medio_usd} visivel={visiveis} /></td>
                  <td style={s.tdNum}>{duracao(a.duracao_mediana_ms)}</td>
                  <td style={s.tdNum}>{duracao(a.duracao_p90_ms)}</td>
                  <td style={{ ...s.tdNum, color: a.erros > 0 ? C.bad : C.text }}>
                    {pct(a.taxa_de_erro_pct)}
                    <div style={{ color: C.neutralMuted, fontSize: 12 }}>{inteiro(a.erros)} erro(s)</div>
                  </td>
                  <td style={s.tdNum}>
                    {decimal(a.reforcos_por_etapa)}
                    <div style={{ color: C.neutralMuted, fontSize: 12 }}>
                      {inteiro(a.reforcos)} em {inteiro(a.etapas)} etapa(s)
                    </div>
                  </td>
                  <td style={s.tdNum}>{pct(a.portao_fechado_na_1a_rodada_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default VisaoGeral;
